import axios from 'axios';
import { apiCacheKey } from '../utils/storageKeys';

// Stale-while-revalidate cache for list pages (ListTemplate's server-fetch mode).
// Two tiers:
//   - in-memory Map: fastest, survives SPA route changes for free
//   - sessionStorage: rehydrates memory on a hard reload / new tab, same tab-scoped
//     lifetime as useApiCache (shares its `api_cache_` key prefix so a single
//     "clear on logout" sweep catches both).
const memoryCache = new Map(); // url -> { data, cachedAt }
const inFlight = new Map();    // url -> Promise<data>

const STALE_MS = 60 * 1000; // background-refetch threshold; cached data still renders past this

function readSessionStorage(url) {
  try {
    const raw = sessionStorage.getItem(apiCacheKey(url));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeSessionStorage(url, entry) {
  try {
    sessionStorage.setItem(apiCacheKey(url), JSON.stringify(entry));
  } catch {
    // sessionStorage full or unavailable — memory cache still works this tab session
  }
}

/** Returns { data, cachedAt, isStale } for a URL, or null if nothing cached. */
export function get(url) {
  let entry = memoryCache.get(url);
  if (!entry) {
    entry = readSessionStorage(url);
    if (entry) memoryCache.set(url, entry);
  }
  if (!entry) return null;
  return { ...entry, isStale: Date.now() - entry.cachedAt > STALE_MS };
}

export function set(url, data) {
  const entry = { data, cachedAt: Date.now() };
  memoryCache.set(url, entry);
  writeSessionStorage(url, entry);
}

/** Removes cache entries. Pass an exact URL, or a predicate (url) => boolean to
 *  bust every cached page/filter variant of a list (e.g. all `/lead/leads/...`). */
export function invalidate(urlOrPredicate) {
  const matches = typeof urlOrPredicate === 'function'
    ? urlOrPredicate
    : (u) => u === urlOrPredicate;

  for (const url of memoryCache.keys()) {
    if (matches(url)) memoryCache.delete(url);
  }
  try {
    const prefix = apiCacheKey('');
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(prefix) && matches(k.slice(prefix.length)))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/** Full clear — call on logout so the next user in this tab never sees
 *  another user's cached list data. */
export function invalidateAll() {
  memoryCache.clear();
  try {
    const prefix = apiCacheKey('');
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(prefix))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/** Fetches a URL with in-flight de-duplication: concurrent callers for the
 *  same URL share one network request instead of firing one each. */
export function fetchDeduped(url, headers) {
  if (inFlight.has(url)) return inFlight.get(url);

  const promise = axios
    .get(url, { headers })
    .then((res) => res.data)
    .finally(() => inFlight.delete(url));

  inFlight.set(url, promise);
  return promise;
}
